import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Store, 
  Search, 
  Eye,
  Calendar,
  ImageIcon,
  Palette,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Cog,
  PenLine,
  RotateCcw,
  ToggleLeft,
  ToggleRight,
  Users,
  Settings,
  Euro,
  Wrench,
  Hash,
  Tag,
  Calculator,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  PriceColorDot, 
  ColorLegend, 
  calculateYearBasedPrice, 
  calculateMonthBasedPrice,
  formatCurrency,
  COLOR_MAP,
  getColorForModelYear
} from '../components/PriceColorSystem';
import YearColorConfig from '../components/YearColorConfig';
import PriceFormulasConfig from '../components/PriceFormulasConfig';
import BatchFormulaDialog from '../components/BatchFormulaDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShowRoom() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [bicycles, setBicycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBike, setSelectedBike] = useState(null);
  
  // View mode: 'showroom' (clean for clients) or 'internal' (detailed for management)
  const [viewMode, setViewMode] = useState('showroom');
  
  // Pricing configuration
  const [pricingFormula, setPricingFormula] = useState('months');
  const [colorLegend, setColorLegend] = useState([]);
  const [avgPriceByModel, setAvgPriceByModel] = useState({});
  const [showLegend, setShowLegend] = useState(false);
  
  // Manual price editing
  const [editingPriceBike, setEditingPriceBike] = useState(null);
  const [manualPriceInput, setManualPriceInput] = useState('');
  
  // Model pricing table
  const [modelPricing, setModelPricing] = useState({});
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModel, setFilterModel] = useState('all');
  const [filterSize, setFilterSize] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [filterColor, setFilterColor] = useState('all');
  const [sortBy, setSortBy] = useState('price_desc');
  const [isColorConfigOpen, setIsColorConfigOpen] = useState(false);
  const [isFormulasConfigOpen, setIsFormulasConfigOpen] = useState(false);
  const [isBatchFormulaOpen, setIsBatchFormulaOpen] = useState(false);

  useEffect(() => {
    fetchBicycles();
    fetchPricingConfig();
    fetchModelPricing();
  }, []);

  const fetchBicycles = async () => {
    try {
      const response = await axios.get(`${API}/bicycles`);
      // Only show active bicycles (not sold)
      const activeBikes = response.data.filter(b => b.status === 'activa');
      setBicycles(activeBikes);
    } catch (error) {
      toast.error('Error al cargar las bicicletas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingConfig = async () => {
    try {
      const [configRes, legendRes, avgPricesRes] = await Promise.all([
        axios.get(`${API}/config/pricing`),
        axios.get(`${API}/config/color-legend`),
        axios.get(`${API}/bicycles/average-prices`)
      ]);
      setPricingFormula(configRes.data.formula);
      setColorLegend(legendRes.data.legend);
      setAvgPriceByModel(avgPricesRes.data);
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    }
  };

  const fetchModelPricing = async () => {
    try {
      const res = await axios.get(`${API}/model-pricing`);
      // Index by "model_name|year" for fast lookup
      const map = {};
      for (const p of res.data) {
        map[`${p.model_name}|${p.year}`] = p.price;
      }
      setModelPricing(map);
    } catch {}
  };

  // Calculate sale price based on selected formula
  const calculateSalePrice = (bike) => {
    // Priority 1: Manual price (highest)
    if (bike.manual_sale_price && bike.manual_sale_price > 0) {
      const yearData = pricingFormula === 'years' ? calculateYearBasedPrice(bike, avgPriceByModel, colorLegend) : null;
      return { 
        ivaPrice: bike.manual_sale_price, 
        isManual: true,
        yearAge: yearData?.yearAge ?? null,
        modelYear: bike.model_year || yearData?.modelYear,
        monthsDiff: pricingFormula !== 'years' ? calculateMonthBasedPrice(bike).monthsDiff : null,
        calculatedColor: yearData?.calculatedColor || getColorForModelYear(bike.model_year)
      };
    }
    
    // Priority 2: Model pricing table (model + year)
    const modelYearKey = `${bike.model}|${bike.model_year}`;
    const tablePriceValue = modelPricing[modelYearKey];
    if (tablePriceValue && tablePriceValue > 0) {
      const yearData = pricingFormula === 'years' ? calculateYearBasedPrice(bike, avgPriceByModel, colorLegend) : null;
      return {
        ivaPrice: tablePriceValue,
        isModelYear: true,
        isManual: false,
        yearAge: yearData?.yearAge ?? null,
        modelYear: bike.model_year || yearData?.modelYear,
        monthsDiff: pricingFormula !== 'years' ? calculateMonthBasedPrice(bike).monthsDiff : null,
        calculatedColor: yearData?.calculatedColor || getColorForModelYear(bike.model_year)
      };
    }
    
    // Priority 3: Year/month-based formula (existing logic)
    if (pricingFormula === 'years') {
      const priceData = calculateYearBasedPrice(bike, avgPriceByModel, colorLegend);
      return { 
        ivaPrice: priceData.ivaPrice, 
        yearAge: priceData.yearAge, 
        percentage: priceData.percentage, 
        modelYear: priceData.modelYear || bike.model_year,
        calculatedColor: priceData.calculatedColor,
        isManual: false 
      };
    }
    const priceData = calculateMonthBasedPrice(bike);
    return { ivaPrice: priceData.ivaPrice, monthsDiff: priceData.monthsDiff, isManual: false };
  };

  // Manual price handlers
  const openPriceEditor = (bike) => {
    setEditingPriceBike(bike);
    setManualPriceInput(bike.manual_sale_price?.toString() || '');
  };

  const saveManualPrice = async () => {
    if (!editingPriceBike || !manualPriceInput) return;
    
    const price = parseFloat(manualPriceInput);
    if (isNaN(price) || price <= 0) {
      toast.error('Introduce un precio válido');
      return;
    }
    
    try {
      await axios.put(`${API}/bicycles/${editingPriceBike.id}/manual-price?price=${price}`);
      toast.success('Precio manual guardado');
      setEditingPriceBike(null);
      fetchBicycles(); // Refresh data
    } catch (error) {
      toast.error('Error al guardar el precio');
    }
  };

  const resetToAutoPrice = async (bikeId) => {
    try {
      await axios.delete(`${API}/bicycles/${bikeId}/manual-price`);
      toast.success('Precio automático restaurado');
      fetchBicycles(); // Refresh data
    } catch (error) {
      toast.error('Error al restaurar precio automático');
    }
  };

  // Calculate age in months
  const calculateAge = (bike) => {
    const purchaseDate = bike.purchase_date ? new Date(bike.purchase_date) : new Date(bike.created_at);
    const now = new Date();
    return (now.getFullYear() - purchaseDate.getFullYear()) * 12 + 
           (now.getMonth() - purchaseDate.getMonth());
  };

  // Get unique values for filters
  const uniqueModels = useMemo(() => 
    [...new Set(bicycles.map(b => b.model))].sort(), 
    [bicycles]
  );
  
  const uniqueSizes = useMemo(() => 
    [...new Set(bicycles.map(b => b.size))].sort(), 
    [bicycles]
  );

  const uniqueYears = useMemo(() => {
    const years = bicycles.map(b => {
      const date = b.purchase_date ? new Date(b.purchase_date) : new Date(b.created_at);
      return date.getFullYear();
    });
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bicycles]);

  const uniqueColors = useMemo(() => {
    // Calculate colors dynamically based on model_year
    const colors = bicycles
      .filter(b => b.model_year)
      .map(b => getColorForModelYear(b.model_year));
    return [...new Set(colors)];
  }, [bicycles]);

  // Filter bicycles
  const filteredBicycles = useMemo(() => {
    let result = bicycles.map(bike => {
      const priceData = calculateSalePrice(bike);
      return {
        ...bike,
        salePrice: priceData.ivaPrice || priceData,
        priceData,
        age: calculateAge(bike),
        year: (bike.purchase_date ? new Date(bike.purchase_date) : new Date(bike.created_at)).getFullYear()
      };
    });

    if (searchTerm) {
      result = result.filter(b => 
        b.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.number.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterModel !== 'all') {
      result = result.filter(b => b.model === filterModel);
    }
    
    if (filterSize !== 'all') {
      result = result.filter(b => b.size === filterSize);
    }

    if (filterYear !== 'all') {
      result = result.filter(b => b.year === parseInt(filterYear));
    }

    if (filterColor !== 'all') {
      result = result.filter(b => b.priceData?.calculatedColor === filterColor);
    }

    // Apply sorting
    switch (sortBy) {
      case 'price_desc':
        result.sort((a, b) => b.salePrice - a.salePrice);
        break;
      case 'price_asc':
        result.sort((a, b) => a.salePrice - b.salePrice);
        break;
      case 'age_desc':
        result.sort((a, b) => b.age - a.age);
        break;
      case 'age_asc':
        result.sort((a, b) => a.age - b.age);
        break;
      case 'year_desc':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'year_asc':
        result.sort((a, b) => a.year - b.year);
        break;
      default:
        break;
    }

    return result;
  }, [bicycles, searchTerm, filterModel, filterSize, filterYear, filterColor, sortBy, pricingFormula, avgPriceByModel, colorLegend, modelPricing]);

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Navigate between bikes in modal
  const currentIndex = selectedBike ? filteredBicycles.findIndex(b => b.id === selectedBike.id) : -1;
  
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setSelectedBike(filteredBicycles[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    if (currentIndex < filteredBicycles.length - 1) {
      setSelectedBike(filteredBicycles[currentIndex + 1]);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="showroom-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900 flex items-center gap-3">
            <Store className="text-orange-500" size={36} />
            2ª Mano
          </h1>
          <p className="text-slate-500 mt-1">
            {viewMode === 'showroom' ? 'Vista para clientes' : 'Vista de gestión interna'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-slate-200 shadow-sm">
            <button
              onClick={() => setViewMode('internal')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'internal' 
                  ? 'bg-slate-800 text-white shadow-sm' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="view-mode-internal"
            >
              <Settings size={16} />
              Gestión
            </button>
            <button
              onClick={() => setViewMode('showroom')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                viewMode === 'showroom' 
                  ? 'bg-orange-500 text-white shadow-sm' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="view-mode-showroom"
            >
              <Users size={16} />
              Sala de Exposición
            </button>
          </div>
          {/* Price Formulas Config (admin only) */}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setIsFormulasConfigOpen(true)} data-testid="formulas-config-btn">
              <Calculator size={16} className="mr-1" /> Fórmulas
            </Button>
          )}
          {/* Batch Formula Button (admin only) */}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setIsBatchFormulaOpen(true)} data-testid="batch-formula-btn">
              <Layers size={16} className="mr-1" /> Lote
            </Button>
          )}
          {/* Color Config (admin only) */}
          {isAdmin && pricingFormula === 'years' && (
            <Button variant="outline" size="sm" onClick={() => setIsColorConfigOpen(true)} data-testid="color-config-btn">
              <Palette size={16} className="mr-1" /> Colores
            </Button>
          )}
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 px-4 py-2 text-sm">
            {filteredBicycles.length} bicicletas
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 bg-white/80 backdrop-blur">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por modelo o número..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={filterModel} onValueChange={setFilterModel}>
              <SelectTrigger className="w-full sm:w-48" data-testid="model-filter">
                <SelectValue placeholder="Modelo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los modelos</SelectItem>
                {uniqueModels.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterSize} onValueChange={setFilterSize}>
              <SelectTrigger className="w-full sm:w-36" data-testid="size-filter">
                <SelectValue placeholder="Talla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las tallas</SelectItem>
                {uniqueSizes.map((size) => (
                  <SelectItem key={size} value={size}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-full sm:w-36" data-testid="year-filter">
                <Calendar size={16} className="mr-2" />
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {uniqueYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pricingFormula === 'years' && (
              <Select value={filterColor} onValueChange={setFilterColor}>
                <SelectTrigger className="w-full sm:w-40" data-testid="color-filter">
                  <SelectValue placeholder="Color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los colores</SelectItem>
                  {uniqueColors.map((color) => {
                    const legendItem = colorLegend.find(l => l.color === color);
                    const yearAge = legendItem?.year_age ?? '?';
                    return (
                      <SelectItem key={color} value={color}>
                        <div className="flex items-center gap-2">
                          <PriceColorDot color={color} hex={legendItem?.color_hex} size="sm" />
                          <span className="capitalize">{color} - Año {yearAge}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-56" data-testid="sort-filter">
                <ArrowUpDown size={16} className="mr-2" />
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="price_desc">
                  <span className="flex items-center gap-2">
                    <ArrowDown size={14} /> Precio: Mayor a menor
                  </span>
                </SelectItem>
                <SelectItem value="price_asc">
                  <span className="flex items-center gap-2">
                    <ArrowUp size={14} /> Precio: Menor a mayor
                  </span>
                </SelectItem>
                <SelectItem value="year_desc">
                  <span className="flex items-center gap-2">
                    <ArrowDown size={14} /> Año: Más reciente
                  </span>
                </SelectItem>
                <SelectItem value="year_asc">
                  <span className="flex items-center gap-2">
                    <ArrowUp size={14} /> Año: Más antiguo
                  </span>
                </SelectItem>
                <SelectItem value="age_asc">
                  <span className="flex items-center gap-2">
                    <ArrowUp size={14} /> Antigüedad: Menor
                  </span>
                </SelectItem>
                <SelectItem value="age_desc">
                  <span className="flex items-center gap-2">
                    <ArrowDown size={14} /> Antigüedad: Mayor
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Color Legend - Hidden by default in ShowRoom */}
      {pricingFormula === 'years' && colorLegend.length > 0 && (
        <div className="space-y-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowLegend(!showLegend)}
            className="text-slate-600"
          >
            <Palette className="w-4 h-4 mr-2" />
            {showLegend ? 'Ocultar leyenda de colores' : 'Ver leyenda de colores'}
          </Button>
          {showLegend && (
            <ColorLegend legend={colorLegend} hidePercentages />
          )}
        </div>
      )}

      {/* Bicycle Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8" />
        </div>
      ) : filteredBicycles.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
          <Store size={48} className="mb-4" />
          <p>No hay bicicletas disponibles</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredBicycles.map((bike) => (
            <Card 
              key={bike.id} 
              className="border-slate-200 hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden"
              onClick={() => setSelectedBike(bike)}
              data-testid={`showroom-card-${bike.id}`}
            >
              {/* Photo */}
              <div className="aspect-square bg-slate-100 relative overflow-hidden">
                {bike.photo_url ? (
                  <img 
                    src={`${BACKEND_URL}${bike.photo_url}`}
                    alt={bike.model}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon size={48} className="text-slate-300" />
                  </div>
                )}
                {/* Price badge overlay */}
                <div className="absolute bottom-3 right-3 flex items-center gap-1">
                  {bike.priceData?.isManual && (
                    <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-xs px-1.5 py-0.5 shadow-lg" title="Precio manual">
                      <PenLine size={12} />
                    </Badge>
                  )}
                  {bike.priceData?.isModelYear && (
                    <Badge className="bg-blue-500 hover:bg-blue-500 text-white text-xs px-1.5 py-0.5 shadow-lg" title="Precio tabla modelo+año">
                      T
                    </Badge>
                  )}
                  <Badge className="bg-green-600 hover:bg-green-600 text-white text-lg px-3 py-1 font-bold shadow-lg">
                    {formatCurrency(bike.salePrice)}
                  </Badge>
                </div>
                {/* Age badge */}
                <div className="absolute top-3 left-3">
                  <Badge variant="outline" className="bg-white/90 text-slate-700 border-slate-200">
                    {pricingFormula === 'years' ? (
                      bike.priceData?.modelYear 
                        ? `Mod. ${bike.priceData.modelYear} (Año ${bike.priceData?.yearAge ?? 0})`
                        : `Año ${bike.priceData?.yearAge ?? 0}`
                    ) : (
                      `${bike.age} ${bike.age === 1 ? 'mes' : 'meses'}`
                    )}
                  </Badge>
                </div>
                {/* Color indicator */}
                {pricingFormula === 'years' && bike.priceData?.calculatedColor && (
                  <div className="absolute top-3 right-3">
                    <PriceColorDot color={bike.priceData.calculatedColor} size="xl" showTooltip yearAge={bike.priceData?.yearAge} />
                  </div>
                )}
                {/* Admin price edit button */}
                {isAdmin && (
                  <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 px-2 bg-white/90 hover:bg-white shadow-md"
                      onClick={(e) => { e.stopPropagation(); openPriceEditor(bike); }}
                      title="Editar precio"
                    >
                      <PenLine size={14} className="mr-1" />
                      Precio
                    </Button>
                  </div>
                )}
              </div>

              {/* Info */}
              <CardContent className="p-4">
                <h3 className="font-bold text-lg text-slate-900 font-['Barlow_Condensed'] truncate">
                  {bike.model}
                </h3>
                <div className="flex items-center justify-between mt-2 text-sm text-slate-500">
                  <span>Talla {bike.size}</span>
                  <span>#{bike.number}</span>
                </div>
                {bike.color && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                    <Palette size={14} />
                    <span>{bike.color}</span>
                  </div>
                )}
                {/* Components */}
                {(bike.wheels || bike.groupset) && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                    {bike.wheels && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-blue-500">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        <span className="truncate">{bike.wheels}</span>
                      </div>
                    )}
                    {bike.groupset && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Cog size={12} className="text-orange-500" />
                        <span className="truncate">{bike.groupset}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Internal Mode: Additional Info */}
                {viewMode === 'internal' && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                    {/* Purchase info */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-slate-400 block">Precio compra</span>
                        <span className="font-semibold text-slate-700">
                          {bike.purchase_price ? formatCurrency(bike.purchase_price) : '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block">Mantenimiento</span>
                        <span className="font-semibold text-slate-700">
                          {formatCurrency(bike.total_maintenance_cost || 0)}
                        </span>
                      </div>
                    </div>
                    {/* Frame number */}
                    {bike.frame_number && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Hash size={12} />
                        <span className="truncate">{bike.frame_number}</span>
                      </div>
                    )}
                    {/* Revisions count */}
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1 text-slate-500">
                        <Wrench size={12} />
                        {bike.revision_count || 0} revisiones
                      </span>
                      {/* Link to detail page */}
                      <Link 
                        to={`/bicycles/${bike.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Ver ficha →
                      </Link>
                    </div>
                    {/* Custom components */}
                    {bike.custom_components && Object.keys(bike.custom_components).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(bike.custom_components).slice(0, 3).map(([name, value]) => (
                          <Badge key={name} variant="outline" className="text-xs py-0 px-1 bg-slate-50">
                            <span className="capitalize">{name}</span>
                          </Badge>
                        ))}
                        {Object.keys(bike.custom_components).length > 3 && (
                          <Badge variant="outline" className="text-xs py-0 px-1 bg-slate-50">
                            +{Object.keys(bike.custom_components).length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedBike} onOpenChange={() => setSelectedBike(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          {selectedBike && (
            <>
              {/* Navigation arrows */}
              {currentIndex > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <ChevronLeft size={24} />
                </button>
              )}
              {currentIndex < filteredBicycles.length - 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <ChevronRight size={24} />
                </button>
              )}

              {/* Photo */}
              <div className="aspect-video bg-slate-100 relative">
                {selectedBike.photo_url ? (
                  <img 
                    src={`${BACKEND_URL}${selectedBike.photo_url}`}
                    alt={selectedBike.model}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon size={64} className="text-slate-300" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <DialogHeader className="mb-6">
                  <DialogTitle className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-900">
                    {selectedBike.model}
                  </DialogTitle>
                  <p className="text-slate-500">#{selectedBike.number} · Talla {selectedBike.size}</p>
                </DialogHeader>

                {/* Price - Highlighted */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border border-green-200">
                  <p className="text-sm text-green-700 font-semibold uppercase tracking-wide mb-1">
                    Precio de Venta
                  </p>
                  <p className="text-5xl font-bold text-green-700 font-['Barlow_Condensed']">
                    {formatCurrency(selectedBike.salePrice)}
                  </p>
                  <p className="text-sm text-green-600 mt-1">IVA incluido</p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Antigüedad</p>
                    <p className="font-bold text-slate-900 text-lg">
                      {selectedBike.age} {selectedBike.age === 1 ? 'mes' : 'meses'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Talla</p>
                    <p className="font-bold text-slate-900 text-lg">{selectedBike.size}</p>
                  </div>
                  {selectedBike.color && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Color</p>
                      <p className="font-bold text-slate-900 text-lg">{selectedBike.color}</p>
                    </div>
                  )}
                  {selectedBike.purchase_date && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Fecha Compra</p>
                      <p className="font-bold text-slate-900 text-sm">{formatDate(selectedBike.purchase_date)}</p>
                    </div>
                  )}
                </div>

                {/* Components */}
                {(selectedBike.wheels || selectedBike.groupset) && (
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-slate-700 mb-4">Componentes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedBike.wheels && (
                        <div className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-blue-600">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                              <line x1="12" y1="2" x2="12" y2="9" stroke="currentColor" strokeWidth="1.5"/>
                              <line x1="12" y1="15" x2="12" y2="22" stroke="currentColor" strokeWidth="1.5"/>
                              <line x1="2" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.5"/>
                              <line x1="15" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600 uppercase">Ruedas</p>
                            <p className="font-semibold text-slate-800">{selectedBike.wheels}</p>
                          </div>
                        </div>
                      )}
                      {selectedBike.groupset && (
                        <div className="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-orange-600">
                              <circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="16" cy="18" r="4" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="8" cy="18" r="2.5" stroke="currentColor" strokeWidth="2"/>
                              <path d="M8 9 L8 15.5" stroke="currentColor" strokeWidth="2"/>
                              <path d="M10.5 17 L12 17" stroke="currentColor" strokeWidth="2"/>
                              <path d="M11 6 L14 10 L14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div>
                            <p className="text-xs text-orange-600 uppercase">Grupo</p>
                            <p className="font-semibold text-slate-800">{selectedBike.groupset}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Counter */}
                <div className="text-center text-sm text-slate-400 mt-6 pt-4 border-t">
                  {currentIndex + 1} de {filteredBicycles.length}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Price Edit Modal */}
      <Dialog open={!!editingPriceBike} onOpenChange={(open) => !open && setEditingPriceBike(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar Precio de Venta</DialogTitle>
          </DialogHeader>
          
          {editingPriceBike && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <p className="font-medium">{editingPriceBike.model}</p>
                <p>#{editingPriceBike.number} - Talla {editingPriceBike.size}</p>
              </div>
              
              {/* Auto price info */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="text-slate-500">Precio automático:</p>
                <p className="font-semibold text-slate-700">
                  {formatCurrency(
                    pricingFormula === 'years' 
                      ? calculateYearBasedPrice(editingPriceBike, avgPriceByModel, colorLegend).ivaPrice
                      : calculateMonthBasedPrice(editingPriceBike).ivaPrice
                  )}
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Precio manual (€)</label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Ej: 3490"
                  value={manualPriceInput}
                  onChange={(e) => setManualPriceInput(e.target.value)}
                  data-testid="manual-price-input"
                />
                <p className="text-xs text-slate-500">
                  Introduce el precio final con IVA incluido
                </p>
              </div>
              
              {editingPriceBike.manual_sale_price && (
                <Button
                  variant="outline"
                  className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={() => {
                    resetToAutoPrice(editingPriceBike.id);
                    setEditingPriceBike(null);
                  }}
                >
                  <RotateCcw size={16} className="mr-2" />
                  Volver al precio automático
                </Button>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPriceBike(null)}>
              Cancelar
            </Button>
            <Button onClick={saveManualPrice}>
              Guardar precio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Year Color Config Modal */}
      <YearColorConfig open={isColorConfigOpen} onClose={() => { setIsColorConfigOpen(false); fetchPricingConfig(); }} />
      
      {/* Price Formulas Config Modal */}
      <PriceFormulasConfig open={isFormulasConfigOpen} onClose={() => setIsFormulasConfigOpen(false)} />
      
      {/* Batch Formula Dialog */}
      <BatchFormulaDialog open={isBatchFormulaOpen} onClose={() => setIsBatchFormulaOpen(false)} onApplied={fetchBicycles} />
    </div>
  );
}
